import { ChatBotRecordRole, ChatConfig, useGetApiAdminChatListChatModelIds } from "@/API";
import { Button, Group, Text, Modal, NumberInput, Select, Switch, Textarea, TextInput, Tooltip, Box, Collapse, ActionIcon, Stack, Paper } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronUp, IconPlus, IconTrash } from "@tabler/icons-react";
import { PropsWithChildren, useEffect } from "react";

export function ChatConfigEditButton(props: { config?: ChatConfig, onChange?: (config: ChatConfig) => void } & PropsWithChildren) {
    const [opened, { open, close }] = useDisclosure(false);
    const [systemPrompt_opened, { toggle: systemPrompt_toggle }] = useDisclosure(false);
    const [additionalPrompts_opened, { toggle: additionalPrompts_toggle }] = useDisclosure(false);
    const form = useForm<ChatConfig>({
        initialValues: props.config,
        validate: {
        }
    });
    const modelList = useGetApiAdminChatListChatModelIds()
    useEffect(() => {
        if (opened) {
            if (props.config) form.setValues(props.config)
            else form.reset()
        }
    }, [opened])

    const additionalPromptsFields = form.getValues().additionalPrompts?.map((item, index) => (
        <Paper withBorder shadow="sm" p="sm">
            <Stack key={index} gap={0}>
                <Select
                    label="角色"
                    defaultValue={ChatBotRecordRole.System}
                    withAsterisk
                    allowDeselect={false}
                    data={[ChatBotRecordRole.System, ChatBotRecordRole.User, ChatBotRecordRole.Assistant]}
                    {...form.getInputProps(`additionalPrompts.${index}.role`)}
                />
                <Textarea
                    label="提示詞"
                    placeholder="prompt"
                    withAsterisk
                    autosize
                    maxRows={10}
                    styles={{ input: { color: 'black' } }}
                    {...form.getInputProps(`additionalPrompts.${index}.message`)}
                />
                <ActionIcon color="red" onClick={() => form.removeListItem('additionalPrompts', index)}>
                    <IconTrash size={16} />
                </ActionIcon>
            </Stack>
        </Paper>
    ));

    return <>
        <div onClick={open}>
            {props.children}
        </div>
        <Modal opened={opened} onClose={close} title="聊天設定" size="xl" closeOnClickOutside={false}>
            <form onSubmit={form.onSubmit((values) => {
                props.onChange?.(values)
                // close()
            })}>
                <Select
                    label="Model"
                    data={modelList.data?.data}
                    styles={{
                        label: { color: "#000000" },
                        option: {
                            color: '#000000',           // 一般選項文字顏色
                            backgroundColor: '#ffffff', // 一般選項背景色

                            '&[data-selected]': {
                                color: '#ffffff',       // 已選中項目文字顏色
                                backgroundColor: '#007bff', // 已選中項目背景色
                            },

                            '&:hover': {
                                color: '#333333',       // 滑鼠懸停文字顏色
                                backgroundColor: '#f0f0f0', // 滑鼠懸停背景色
                            },

                            '&[data-disabled]': {
                                color: '#cccccc',       // 禁用選項文字顏色
                            }
                        },

                        dropdown: {
                            backgroundColor: '#ffffff',  // 整個下拉選單背景
                            border: '1px solid #dee2e6', // 下拉選單邊框
                        },

                        input: {
                            color: '#000000',           // 輸入框文字顏色
                        }
                    }}
                    {...form.getInputProps('modelId')}
                />
                <TextInput
                    label="名稱"
                    placeholder="optional"
                    styles={{
                        label: { color: "#000000" },
                        input: { color: "#000000" }
                    }}
                    {...form.getInputProps('systemName')}
                />
                <Tooltip color="gray" position="top-start" multiline events={{ hover: true, focus: false, touch: true }} style={{ whiteSpace: "pre-line" }} label={"變數:\n{{$Context}}:聊天室Context\n{{$Now}}:當前時間\n範例:2025/1/1 上午 00:00:00 \n{{$UtcNow}}:當前UTC時間\n範例:2025-01-01T00:00:000.0000000Z"}>
                    <Stack gap={0} onClick={systemPrompt_toggle}>
                        <Group gap={0}>
                            <Text>
                                系統提示詞
                            </Text>
                            <ActionIcon variant="transparent">
                                {systemPrompt_opened ?
                                    <IconChevronUp stroke={2} /> : <IconChevronDown stroke={2} />
                                }
                            </ActionIcon>
                        </Group>
                        <Text size="xs" fw={300} c="gray">
                            系統提示詞，用於定義聊天機器人性格、對話規則等
                        </Text>
                    </Stack>
                </Tooltip>
                <Collapse in={systemPrompt_opened}>
                    <Textarea
                        autosize
                        maxRows={20}
                        styles={{ input: { color: 'black' } }}
                        {...form.getInputProps('systemPrompt')}
                    />
                </Collapse>
                <Tooltip color="gray" position="top-start" multiline events={{ hover: true, focus: false, touch: true }} style={{ whiteSpace: "pre-line" }} label={"變數:\n{{$Context}}:聊天室Context\n{{$Now}}:當前時間\n範例:2025/1/1 上午 00:00:00 \n{{$UtcNow}}:當前UTC時間\n範例:2025-01-01T00:00:000.0000000Z"}>
                    <Stack gap={0} onClick={additionalPrompts_toggle}>
                        <Group gap={0}>
                            <Text>
                                額外提示詞
                            </Text>
                            <ActionIcon variant="transparent">
                                {additionalPrompts_opened ?
                                    <IconChevronUp stroke={2} /> : <IconChevronDown stroke={2} />
                                }
                            </ActionIcon>
                        </Group>
                        <Text size="xs" fw={300} c="gray">
                            額外提示詞，放在系統提示詞之後
                        </Text>
                    </Stack>
                </Tooltip>
                <Collapse in={additionalPrompts_opened}>
                    {additionalPromptsFields}
                    <ActionIcon color="green" mt="md" onClick={() =>
                        form.insertListItem('additionalPrompts', {})
                    }>
                        <IconPlus size={16} />
                    </ActionIcon>
                </Collapse>
                <NumberInput
                    label="最大歷史紀錄個數"
                    description="會將過去問答納入下次回答的依據，一問一答算兩個個數"
                    min={0}
                    max={20}
                    step={1}
                    {...form.getInputProps('maxHistoryRecordsCount')}
                />
                <NumberInput
                    label="Temperature"
                    min={0}
                    max={1}
                    step={0.1}
                    {...form.getInputProps('temperature')}
                />
                <NumberInput
                    label="TopP"
                    min={0}
                    max={1}
                    step={0.1}
                    {...form.getInputProps('topP')}
                />
                <Group justify="flex-end" mt="md">
                    <Button type="submit">保存</Button>
                </Group>
            </form>
        </Modal>
    </>
}